/**
 * Key images — flagged clinically significant images per study.
 * Thin API wrapper around the keyimages.* endpoints on the paired
 * image service. (EDGEPACS-only feature; no DICOMweb equivalent yet.)
 */
import { apiCall } from '../api/client';

export interface KeyImage {
  keyImageId: number;
  seriesId: number;
  imageIndex: number;
  userId: number;
  note?: string;
  createdAt?: string;
}

export async function listKeyImages(studyId: number): Promise<KeyImage[]> {
  const res = await apiCall<KeyImage>('keyimages.list', { studyId });
  return res.s === 'ok' ? (res.data || []) : [];
}

/** Idempotent toggle — call the same args twice and the state flips.
 *  Returns `removed: true` if the call deleted an existing flag. */
export async function toggleKeyImage(
  studyId: number,
  seriesId: number,
  imageIndex: number,
  note = ''
): Promise<{ removed: boolean; keyImageId: number } | null> {
  const res = await apiCall<{ removed: boolean; keyImageId: number }>(
    'keyimages.toggle',
    { studyId, seriesId, imageIndex, note }
  );
  if (res.s !== 'ok') return null;
  return res.data?.[0] ?? null;
}

export async function updateKeyImageNote(keyImageId: number, note: string): Promise<boolean> {
  const res = await apiCall('keyimages.update', { keyImageId, note });
  return res.s === 'ok';
}

/** Build a thumbnail URL for a specific series/image pair. */
export function keyImageThumbUrl(
  serverUrl: string,
  seriesId: number,
  imageIndex: number,
  acctoken: string | null
): string {
  // Reuse the existing thumbnail endpoint. It returns the middle image of the
  // series, so not strictly the flagged image, but good enough until we add
  // an image-index param. TODO: extend HandleThumbnailGet to accept imageIndex.
  return `${serverUrl}/atlrestapi/v2/thumbnail?seriesId=${seriesId}&imageIndex=${imageIndex}&acctoken=${acctoken}`;
}

/** Key = `${seriesId}:${imageIndex}` for quick "is this flagged?" lookups. */
export function keyImageKey(seriesId: number, imageIndex: number): string {
  return `${seriesId}:${imageIndex}`;
}

export function keyImageSet(list: KeyImage[]): Set<string> {
  return new Set(list.map((k) => keyImageKey(k.seriesId, k.imageIndex)));
}
